import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ModernMissionForm = ({ onMissionCreated, editMission, onEditComplete }) => {
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    type_mission: 'interim',
    date_debut: '',
    date_fin: '',
    duree_heures: '',
    duree_jours: '',
    remuneration_base: '',
    ville: '',
    code_postal: '',
    competences_requises: [],
    montage_complexe_requis: false,
    experience_min: 0,
    permis_requis: false,
    vehicule_requis: false,
    horaires: '',
    avantages: [],
    urgente: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const competencesOptions = [
    'Optométrie', 'Contactologie', 'Basse vision', 'Optique pédiatrique',
    'Réfraction', 'Adaptation lentilles', 'Lunetterie', 'Solaire',
    'Vente', 'Relation client', 'Gestion stock', 'Montage complexe',
    'Verres progressifs', 'Montures percées', 'Personnalisation'
  ];

  const avantagesOptions = [
    'Tickets restaurant', 'Mutuelle', 'Transport remboursé', 
    'Formation continue', 'Prime de performance', 'Télétravail partiel'
  ];

  useEffect(() => {
    if (editMission) {
      setFormData({
        ...editMission,
        date_debut: editMission.date_debut ? new Date(editMission.date_debut).toISOString().split('T')[0] : '',
        date_fin: editMission.date_fin ? new Date(editMission.date_fin).toISOString().split('T')[0] : '',
      });
    }
  }, [editMission]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const missionData = {
        ...formData,
        date_debut: new Date(formData.date_debut).toISOString(),
        date_fin: formData.date_fin ? new Date(formData.date_fin).toISOString() : null,
        duree_heures: formData.duree_heures ? parseInt(formData.duree_heures) : null,
        duree_jours: formData.duree_jours ? parseInt(formData.duree_jours) : null,
        remuneration_base: parseFloat(formData.remuneration_base),
        experience_min: parseInt(formData.experience_min)
      };

      const response = await axios.post(`${API}/missions`, missionData);
      
      onMissionCreated && onMissionCreated(response.data);
      onEditComplete && onEditComplete();
      
      if (!editMission) {
        setFormData({
          titre: '', description: '', type_mission: 'interim',
          date_debut: '', date_fin: '', duree_heures: '', duree_jours: '',
          remuneration_base: '', retrocession_pourcentage: 0,
          retrocession_description: '', ville: '', code_postal: '',
          competences_requises: [], montage_complexe_requis: false,
          experience_min: 0, permis_requis: false, vehicule_requis: false,
          horaires: '', avantages: [], urgente: false
        });
      }
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleArrayToggle = (item, arrayName) => {
    setFormData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].includes(item)
        ? prev[arrayName].filter(i => i !== item)
        : [...prev[arrayName], item]
    }));
  };

  return (
    <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 backdrop-blur-sm border border-slate-600/50 rounded-xl p-8 shadow-xl">
      <h3 className="text-2xl font-bold text-white mb-8 flex items-center">
        <span className="mr-3">🎯</span>
        {editMission ? 'Modifier la mission' : 'Créer une nouvelle mission'}
      </h3>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Informations de base */}
        <div className="bg-slate-700/30 rounded-lg p-6 border border-slate-600/30">
          <h4 className="text-lg font-semibold text-white mb-4">📝 Informations générales</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Titre de la mission *
              </label>
              <input
                type="text"
                name="titre"
                value={formData.titre}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: Opticien diplômé H/F - Remplacement"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Type de mission *
                </label>
                <select
                  name="type_mission"
                  value={formData.type_mission}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="interim">Mission d'intérim</option>
                  <option value="cdd">CDD</option>
                  <option value="alternance">Alternance</option>
                  <option value="remplacement">Remplacement ponctuel</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Expérience minimum (années)
                </label>
                <input
                  type="number"
                  name="experience_min"
                  value={formData.experience_min}
                  onChange={handleInputChange}
                  min="0"
                  max="20"
                  className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Description détaillée *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                placeholder="Décrivez en détail la mission, l'environnement de travail, les responsabilités..."
                required
              />
            </div>
          </div>
        </div>

        {/* Période et rémunération */}
        <div className="bg-slate-700/30 rounded-lg p-6 border border-slate-600/30">
          <h4 className="text-lg font-semibold text-white mb-4">💰 Période et rémunération</h4>
          
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Date de début *
              </label>
              <input
                type="date"
                name="date_debut"
                value={formData.date_debut}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Date de fin (optionnel)
              </label>
              <input
                type="date"
                name="date_fin"
                value={formData.date_fin}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Durée (heures/semaine)
              </label>
              <input
                type="number"
                name="duree_heures"
                value={formData.duree_heures}
                onChange={handleInputChange}
                min="1"
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                placeholder="35"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Rémunération de base (€/heure) *
              </label>
              <input
                type="number"
                name="remuneration_base"
                value={formData.remuneration_base}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                placeholder="15.50"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Durée (jours)
              </label>
              <input
                type="number"
                name="duree_jours"
                value={formData.duree_jours}
                onChange={handleInputChange}
                min="1"
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: 30"
              />
            </div>
          </div>
        </div>

        {/* Localisation */}
        <div className="bg-slate-700/30 rounded-lg p-6 border border-slate-600/30">
          <h4 className="text-lg font-semibold text-white mb-4">📍 Localisation</h4>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Ville *
              </label>
              <input
                type="text"
                name="ville"
                value={formData.ville}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Code postal *
              </label>
              <input
                type="text"
                name="code_postal"
                value={formData.code_postal}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>
        </div>

        {/* Compétences et exigences */}
        <div className="bg-slate-700/30 rounded-lg p-6 border border-slate-600/30">
          <h4 className="text-lg font-semibold text-white mb-4">🎯 Compétences requises</h4>
          
          {/* Montage complexe - Nouvelle fonctionnalité */}
          <div className="mb-6 p-4 bg-blue-600/20 border border-blue-500/30 rounded-lg">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="montage_complexe_requis"
                checked={formData.montage_complexe_requis}
                onChange={handleInputChange}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <div>
                <span className="text-white font-medium">🔧 Montage complexe requis</span>
                <p className="text-blue-200 text-sm">Verres progressifs, montures percées, personnalisations spéciales</p>
              </div>
            </label>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {competencesOptions.map((competence) => (
              <label key={competence} className="flex items-center space-x-2 cursor-pointer p-2 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.competences_requises.includes(competence)}
                  onChange={() => handleArrayToggle(competence, 'competences_requises')}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-200">{competence}</span>
              </label>
            ))}
          </div>

          {/* Exigences transport */}
          <div className="grid md:grid-cols-2 gap-4">
            <label className="flex items-center space-x-3 cursor-pointer p-3 bg-slate-600/30 rounded-lg">
              <input
                type="checkbox"
                name="permis_requis"
                checked={formData.permis_requis}
                onChange={handleInputChange}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-slate-200">🚗 Permis de conduire requis</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer p-3 bg-slate-600/30 rounded-lg">
              <input
                type="checkbox"
                name="vehicule_requis"
                checked={formData.vehicule_requis}
                onChange={handleInputChange}
                className="rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="text-slate-200">🚙 Véhicule personnel requis</span>
            </label>
          </div>
        </div>

        {/* Avantages et options */}
        <div className="bg-slate-700/30 rounded-lg p-6 border border-slate-600/30">
          <h4 className="text-lg font-semibold text-white mb-4">✨ Avantages et conditions</h4>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Horaires de travail
            </label>
            <input
              type="text"
              name="horaires"
              value={formData.horaires}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: 9h-18h, du lundi au samedi"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-200 mb-3">
              Avantages proposés
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {avantagesOptions.map((avantage) => (
                <label key={avantage} className="flex items-center space-x-2 cursor-pointer p-2 bg-slate-600/30 rounded-lg hover:bg-slate-600/50 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.avantages.includes(avantage)}
                    onChange={() => handleArrayToggle(avantage, 'avantages')}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-200">{avantage}</span>
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center space-x-3 cursor-pointer p-3 bg-orange-600/20 border border-orange-500/30 rounded-lg">
            <input
              type="checkbox"
              name="urgente"
              checked={formData.urgente}
              onChange={handleInputChange}
              className="rounded text-orange-600 focus:ring-orange-500"
            />
            <div>
              <span className="text-orange-200 font-medium">🚨 Mission urgente</span>
              <p className="text-orange-300 text-sm">Prise de poste immédiate souhaitée</p>
            </div>
          </label>
        </div>

        {error && (
          <div className="p-4 bg-red-600/20 border border-red-500/30 text-red-200 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 shadow-lg"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Publication...</span>
              </div>
            ) : (
              editMission ? 'Modifier la mission' : 'Publier la mission'
            )}
          </button>
          
          {editMission && (
            <button
              type="button"
              onClick={onEditComplete}
              className="px-6 py-4 border border-slate-500 rounded-lg text-slate-300 hover:bg-slate-700/50 transition-colors"
            >
              Annuler
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export const ModernMissionList = () => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingMission, setEditingMission] = useState(null);

  useEffect(() => {
    fetchMissions();
  }, []);

  const fetchMissions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/magasins/missions`);
      setMissions(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des missions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMissionCreated = (newMission) => {
    setMissions(prev => [newMission, ...prev]);
    setShowForm(false);
  };

  const handleEditMission = (mission) => {
    setEditingMission(mission);
    setShowForm(true);
  };

  const handleEditComplete = () => {
    setShowForm(false);
    setEditingMission(null);
    fetchMissions();
  };

  if (showForm) {
    return (
      <ModernMissionForm 
        onMissionCreated={handleMissionCreated}
        editMission={editingMission}
        onEditComplete={handleEditComplete}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white flex items-center">
          <span className="mr-3">🎯</span>
          Mes Missions ({missions.length})
        </h3>
        <button
          onClick={() => setShowForm(true)}
          className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-3 rounded-lg transition-all duration-300 shadow-lg flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Nouvelle Mission</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : missions.length === 0 ? (
        <div className="bg-slate-700/30 rounded-xl p-12 text-center border border-slate-600/30">
          <div className="text-6xl mb-4">🎯</div>
          <h3 className="text-xl font-semibold text-white mb-2">Aucune mission créée</h3>
          <p className="text-slate-400 mb-6">
            Commencez par créer votre première mission pour attirer les meilleurs opticiens.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-3 rounded-lg transition-all duration-300"
          >
            Créer ma première mission
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {missions.map((mission) => (
            <MissionCard key={mission.id} mission={mission} onEdit={handleEditMission} />
          ))}
        </div>
      )}
    </div>
  );
};

const MissionCard = ({ mission, onEdit }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-600/20 text-green-400 border-green-500/30';
      case 'pourvue': return 'bg-blue-600/20 text-blue-400 border-blue-500/30';
      case 'terminee': return 'bg-gray-600/20 text-gray-400 border-gray-500/30';
      default: return 'bg-yellow-600/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'active': return 'Active';
      case 'pourvue': return 'Pourvue';
      case 'terminee': return 'Terminée';
      default: return 'En attente';
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'interim': return 'bg-blue-100 text-blue-800';
      case 'cdd': return 'bg-green-100 text-green-800';
      case 'alternance': return 'bg-purple-100 text-purple-800';
      case 'remplacement': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'interim': return 'Intérim';
      case 'cdd': return 'CDD';
      case 'alternance': return 'Alternance';
      case 'remplacement': return 'Remplacement';
      default: return type;
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <h4 className="text-lg font-semibold text-white">
              {mission.titre}
            </h4>
            {mission.urgente && (
              <span className="px-2 py-1 bg-red-600/20 text-red-400 border border-red-500/30 rounded-full text-xs font-medium">
                🚨 URGENT
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2 mb-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(mission.status)}`}>
              {getStatusLabel(mission.status)}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(mission.type_mission)}`}>
              {getTypeLabel(mission.type_mission)}
            </span>
            <span className="px-3 py-1 bg-slate-600/50 text-slate-300 rounded-full text-xs font-medium">
              📍 {mission.ville} ({mission.code_postal})
            </span>
            <span className="px-3 py-1 bg-slate-600/50 text-slate-300 rounded-full text-xs font-medium">
              💼 {mission.experience_min}+ ans
            </span>
            <span className="px-3 py-1 bg-slate-600/50 text-slate-300 rounded-full text-xs font-medium">
              💰 {mission.remuneration_base}€/h
            </span>
            {mission.montage_complexe_requis && (
              <span className="px-3 py-1 bg-purple-600/20 text-purple-400 border border-purple-500/30 rounded-full text-xs font-medium">
                🔧 Montage complexe
              </span>
            )}
          </div>
        </div>
        
        <button
          onClick={() => onEdit(mission)}
          className="ml-4 text-blue-400 hover:text-blue-300 transition-colors p-2 rounded-lg hover:bg-blue-600/20"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      </div>

      <p className="text-slate-300 mb-4 line-clamp-2">
        {mission.description}
      </p>

      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div className="space-y-2">
          <div className="flex items-center text-slate-300">
            <span className="w-4 mr-2">📅</span>
            <span>Début: {formatDate(mission.date_debut)}</span>
          </div>
          {mission.date_fin && (
            <div className="flex items-center text-slate-300">
              <span className="w-4 mr-2">🏁</span>
              <span>Fin: {formatDate(mission.date_fin)}</span>
            </div>
          )}
          {mission.duree_heures && (
            <div className="flex items-center text-slate-300">
              <span className="w-4 mr-2">⏰</span>
              <span>{mission.duree_heures}h/semaine</span>
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center text-slate-300">
            <span className="w-4 mr-2">👥</span>
            <span>{mission.candidatures_count || 0} candidatures</span>
          </div>
          {mission.horaires && (
            <div className="flex items-center text-slate-300">
              <span className="w-4 mr-2">🕐</span>
              <span>{mission.horaires}</span>
            </div>
          )}
          <div className="flex items-center text-slate-400">
            <span className="w-4 mr-2">📝</span>
            <span>Publié le {formatDate(mission.created_at)}</span>
          </div>
        </div>
      </div>

      {mission.competences_requises && mission.competences_requises.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-600/30">
          <p className="text-sm text-slate-400 mb-2">Compétences requises:</p>
          <div className="flex flex-wrap gap-2">
            {mission.competences_requises.slice(0, 5).map((competence) => (
              <span key={competence} className="px-2 py-1 bg-blue-600/20 text-blue-300 rounded text-xs border border-blue-500/30">
                {competence}
              </span>
            ))}
            {mission.competences_requises.length > 5 && (
              <span className="px-2 py-1 bg-slate-600/50 text-slate-400 rounded text-xs">
                +{mission.competences_requises.length - 5} autres
              </span>
            )}
          </div>
        </div>
      )}

      {mission.avantages && mission.avantages.length > 0 && (
        <div className="mt-3">
          <p className="text-sm text-slate-400 mb-2">Avantages:</p>
          <div className="flex flex-wrap gap-2">
            {mission.avantages.slice(0, 3).map((avantage) => (
              <span key={avantage} className="px-2 py-1 bg-emerald-600/20 text-emerald-300 rounded text-xs border border-emerald-500/30">
                ✅ {avantage}
              </span>
            ))}
            {mission.avantages.length > 3 && (
              <span className="px-2 py-1 bg-slate-600/50 text-slate-400 rounded text-xs">
                +{mission.avantages.length - 3} autres
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const ModernMissionSearch = () => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilters, setSearchFilters] = useState({
    ville: '',
    type_mission: '',
    montage_complexe: false
  });

  useEffect(() => {
    fetchMissions();
  }, [searchFilters]);

  const fetchMissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchFilters.ville) params.append('ville', searchFilters.ville);
      if (searchFilters.type_mission) params.append('type_mission', searchFilters.type_mission);
      if (searchFilters.montage_complexe) params.append('montage_complexe', 'true');
      
      const response = await axios.get(`${API}/missions?${params.toString()}`);
      setMissions(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des missions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSearchFilters(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleApply = async (missionId) => {
    try {
      await axios.post(`${API}/applications`, {
        mission_id: missionId,
        message: "Je suis intéressé(e) par cette mission et souhaiterais en discuter avec vous."
      });
      
      // Mise à jour locale du compteur de candidatures
      setMissions(prev => prev.map(m => 
        m.id === missionId 
          ? { ...m, candidatures_count: (m.candidatures_count || 0) + 1 }
          : m
      ));
      
      alert('Candidature envoyée avec succès !');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de l\'envoi de la candidature');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
          <span className="mr-3">🔍</span>
          Rechercher des missions
        </h3>
        
        {/* Filtres de recherche améliorés */}
        <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 mb-8 shadow-lg">
          <h4 className="text-lg font-semibold text-white mb-4">🎛️ Filtres de recherche</h4>
          
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Ville
              </label>
              <input
                type="text"
                name="ville"
                value={searchFilters.ville}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                placeholder="Paris, Lyon..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Type de mission
              </label>
              <select
                name="type_mission"
                value={searchFilters.type_mission}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 bg-slate-600/50 border border-slate-500/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Tous types</option>
                <option value="interim">Intérim</option>
                <option value="cdd">CDD</option>
                <option value="alternance">Alternance</option>
                <option value="remplacement">Remplacement</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center space-x-3 cursor-pointer p-3 bg-slate-600/30 rounded-lg w-full">
                <input
                  type="checkbox"
                  name="montage_complexe"
                  checked={searchFilters.montage_complexe}
                  onChange={handleFilterChange}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-slate-200 text-sm">🔧 Montage complexe</span>
              </label>
            </div>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-slate-400">
            <span>📊 {missions.length} mission(s) trouvée(s)</span>
            {(searchFilters.ville || searchFilters.type_mission || searchFilters.montage_complexe) && (
              <button
                onClick={() => setSearchFilters({ ville: '', type_mission: '', montage_complexe: false })}
                className="text-blue-400 hover:text-blue-300"
              >
                🗑️ Réinitialiser les filtres
              </button>
            )}
          </div>
        </div>

        {/* Liste des missions */}
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : missions.length === 0 ? (
          <div className="bg-slate-700/30 rounded-xl p-12 text-center border border-slate-600/30">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-white mb-2">Aucune mission trouvée</h3>
            <p className="text-slate-400">
              Essayez de modifier vos critères de recherche pour voir plus d'opportunités.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {missions.map((mission) => (
              <SearchMissionCard key={mission.id} mission={mission} onApply={handleApply} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SearchMissionCard = ({ mission, onApply }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'interim': return 'bg-blue-100 text-blue-800';
      case 'cdd': return 'bg-green-100 text-green-800';
      case 'alternance': return 'bg-purple-100 text-purple-800';
      case 'remplacement': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'interim': return 'Intérim';
      case 'cdd': return 'CDD';
      case 'alternance': return 'Alternance';
      case 'remplacement': return 'Remplacement';
      default: return type;
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-800/50 to-slate-700/50 border border-slate-600/50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-blue-500/50">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            <h4 className="text-xl font-semibold text-white">
              {mission.titre}
            </h4>
            {mission.urgente && (
              <span className="px-3 py-1 bg-red-600/20 text-red-400 border border-red-500/30 rounded-full text-sm font-medium animate-pulse">
                🚨 URGENT
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(mission.type_mission)}`}>
              {getTypeLabel(mission.type_mission)}
            </span>
            <span className="px-3 py-1 bg-slate-600/50 text-slate-300 rounded-full text-sm font-medium">
              📍 {mission.ville}
            </span>
            <span className="px-3 py-1 bg-slate-600/50 text-slate-300 rounded-full text-sm font-medium">
              💼 {mission.experience_min}+ ans
            </span>
            <span className="px-3 py-1 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-full text-sm font-medium">
              💰 {mission.remuneration_base}€/h
            </span>
            {mission.montage_complexe_requis && (
              <span className="px-3 py-1 bg-purple-600/20 text-purple-400 border border-purple-500/30 rounded-full text-sm font-medium">
                🔧 Montage complexe
              </span>
            )}
          </div>
        </div>
        
        <button
          onClick={() => onApply(mission.id)}
          className="ml-4 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-6 py-3 rounded-lg transition-all duration-300 shadow-lg flex items-center space-x-2 font-medium"
        >
          <span>📤</span>
          <span>Postuler</span>
        </button>
      </div>

      <p className="text-slate-300 mb-6 leading-relaxed">
        {mission.description}
      </p>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="space-y-2">
          <h5 className="font-medium text-white flex items-center">
            <span className="mr-2">📅</span>
            Période
          </h5>
          <p className="text-slate-300 text-sm">Du {formatDate(mission.date_debut)}</p>
          {mission.date_fin && (
            <p className="text-slate-300 text-sm">au {formatDate(mission.date_fin)}</p>
          )}
          {mission.duree_heures && (
            <p className="text-slate-300 text-sm">{mission.duree_heures}h/semaine</p>
          )}
        </div>
        
        <div className="space-y-2">
          <h5 className="font-medium text-white flex items-center">
            <span className="mr-2">💰</span>
            Rémunération
          </h5>
          <p className="text-emerald-400 text-sm font-medium">{mission.remuneration_base}€/heure</p>
        </div>
        
        <div className="space-y-2">
          <h5 className="font-medium text-white flex items-center">
            <span className="mr-2">ℹ️</span>
            Informations
          </h5>
          <p className="text-slate-300 text-sm">{mission.candidatures_count || 0} candidatures</p>
          {mission.horaires && (
            <p className="text-slate-300 text-sm">{mission.horaires}</p>
          )}
        </div>
      </div>

      {mission.competences_requises && mission.competences_requises.length > 0 && (
        <div className="mb-4">
          <h5 className="font-medium text-white mb-2 flex items-center">
            <span className="mr-2">🎯</span>
            Compétences requises
          </h5>
          <div className="flex flex-wrap gap-2">
            {mission.competences_requises.map((competence) => (
              <span key={competence} className="px-3 py-1 bg-blue-600/20 text-blue-300 rounded-lg text-sm border border-blue-500/30">
                {competence}
              </span>
            ))}
          </div>
        </div>
      )}

      {mission.avantages && mission.avantages.length > 0 && (
        <div className="mb-4">
          <h5 className="font-medium text-white mb-2 flex items-center">
            <span className="mr-2">✨</span>
            Avantages
          </h5>
          <div className="flex flex-wrap gap-2">
            {mission.avantages.map((avantage) => (
              <span key={avantage} className="px-3 py-1 bg-emerald-600/20 text-emerald-300 rounded-lg text-sm border border-emerald-500/30">
                ✅ {avantage}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="text-xs text-slate-500 pt-4 border-t border-slate-600/30">
        Publié le {formatDate(mission.created_at)}
      </div>
    </div>
  );
};